<template>
  <transition name="giw-fade">
    <div
      v-if="isGuest && open"
      class="giw-overlay theme-turquoise"
      @click.self="skip"
    >
      <div
        class="giw-card"
        role="dialog"
        aria-modal="true"
        aria-label="Welcome to Alice"
      >
        <button
          class="giw-close"
          type="button"
          aria-label="Close"
          @click="skip"
        >
          ×
        </button>

        <!-- ============ Visual (recreated dashboard fragments) ============ -->
        <div class="giw-visual">
          <!-- Step 1 — where things are -->
          <div v-if="step === 0" class="giw-mock">
            <div class="giw-projcard">
              <span class="giw-projcard__mark" />
              <div>
                <div class="giw-projcard__title">KPN Contractovername</div>
                <span class="giw-tag">In progress</span>
              </div>
            </div>
            <div class="giw-tabs">
              <span class="giw-tab giw-tab--on">Chat</span>
              <span class="giw-tab">Insights</span>
              <span class="giw-tab">Files</span>
              <span class="giw-tab">Notes</span>
            </div>
          </div>

          <!-- Step 2 — ask Alice -->
          <div v-else-if="step === 1" class="giw-mock">
            <div class="giw-toggle">
              <span class="giw-toggle__pill giw-toggle__pill--on"
                >Personal</span
              >
              <span class="giw-toggle__pill">Team</span>
            </div>
            <div class="giw-bubble giw-bubble--user">
              How many people struggled with onboarding?
            </div>
            <div class="giw-bubble giw-bubble--alice">
              <span class="giw-line" style="width: 90%" />
              <span class="giw-line" style="width: 70%" />
            </div>
            <div class="giw-composer">
              <span class="giw-composer__slash">/</span>
              <span class="giw-composer__ph"
                >Ask anything about your data…</span
              >
            </div>
          </div>

          <!-- Step 3 — collaborate -->
          <div v-else-if="step === 2" class="giw-mock">
            <div class="giw-msg">
              <span class="giw-avatar" style="background: var(--vp-blauw-100)"
                >M</span
              >
              <span class="giw-msg__text"
                >Can you pull the top frustrations?</span
              >
            </div>
            <div class="giw-msg giw-msg--me">
              <span class="giw-msg__text giw-msg__text--me"
                >On it — saving the key ones.</span
              >
              <span
                class="giw-avatar"
                style="background: var(--vp-turquoise-120)"
                >J</span
              >
            </div>
            <div class="giw-note">
              <Icon name="bookmark" :size="14" />
              <span>Saved note · Checkout confusion</span>
            </div>
          </div>

          <!-- Step 4 — good to know -->
          <div v-else class="giw-mock giw-mock--center">
            <span class="giw-chip">Access until 14 Aug 2026</span>
            <div class="giw-meter">
              <div class="giw-meter__fill" style="width: 30%" />
            </div>
            <div class="giw-meter__label">0.6M / 2M AI tokens used</div>
          </div>
        </div>

        <!-- ===================== Copy ===================== -->
        <div class="giw-body">
          <div class="giw-eyebrow">
            Step {{ step + 1 }} of {{ steps.length }}
          </div>
          <h2 class="giw-title">{{ steps[step].title }}</h2>
          <p class="giw-text">{{ steps[step].body }}</p>
        </div>

        <!-- ===================== Footer ===================== -->
        <div class="giw-foot">
          <div class="giw-dots" aria-hidden="true">
            <span
              v-for="(s, i) in steps"
              :key="i"
              class="giw-dot"
              :class="{ 'giw-dot--on': i === step }"
            />
          </div>
          <div class="giw-actions">
            <button
              v-if="step > 0"
              class="giw-btn giw-btn--ghost"
              type="button"
              @click="back"
            >
              Back
            </button>
            <button
              v-if="!isLast"
              class="giw-btn giw-btn--ghost"
              type="button"
              @click="skip"
            >
              Skip
            </button>
            <button class="giw-btn" type="button" @click="next">
              {{ isLast ? "Get started" : "Next" }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import Icon from "@/components/alice/Icon.vue";

export default {
  name: "GuestIntroWizard",
  components: { Icon },
  data() {
    return {
      step: 0,
      steps: [
        {
          title: "Welcome to Alice",
          body: "You've been invited to collaborate on research in Alice. Your projects live in the sidebar — open one to explore its chat, insights, files and notes.",
        },
        {
          title: "Ask Alice anything",
          body: "Chat with Alice about the project's research data. Switch between your private thread and the shared Team thread, and type “/” to apply a skill.",
        },
        {
          title: "Work with the team",
          body: "Use Team chat to collaborate with your Valsplat researcher, and bookmark useful answers as Notes so nothing gets lost.",
        },
        {
          title: "Good to know",
          body: "Your access is temporary and has an AI usage budget. When it runs low or expires, just ask your Valsplat contact to extend it.",
        },
      ],
    };
  },
  computed: {
    isGuest() {
      return this.$store.getters.isGuest;
    },
    open() {
      return this.$store.state.guestIntroOpen;
    },
    isLast() {
      return this.step === this.steps.length - 1;
    },
  },
  watch: {
    open(v) {
      if (v) this.step = 0;
    },
  },
  methods: {
    next() {
      if (this.isLast) this.finish();
      else this.step += 1;
    },
    back() {
      if (this.step > 0) this.step -= 1;
    },
    skip() {
      this.finish();
    },
    finish() {
      this.$store.dispatch("markGuestIntroSeen");
    },
  },
};
</script>

<style scoped>
.giw-overlay {
  position: fixed;
  inset: 0;
  z-index: 3000;
  background: rgba(20, 25, 25, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}
.giw-card {
  position: relative;
  width: 100%;
  max-width: 460px;
  background: var(--vp-white);
  border: 1px solid var(--vp-black);
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.28);
  font-family: var(--font-body);
}
.giw-close {
  position: absolute;
  top: 8px;
  right: 10px;
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  font-size: 22px;
  line-height: 1;
  color: var(--fg-3);
  cursor: pointer;
}
.giw-close:hover {
  color: var(--vp-black);
}

/* ---- Visual panel ---- */
.giw-visual {
  height: 190px;
  background: var(--bg-muted);
  border-bottom: 1px solid var(--border-hairline);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}
.giw-mock {
  width: 100%;
  max-width: 300px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.giw-mock--center {
  align-items: center;
  gap: 14px;
}

/* project card */
.giw-projcard {
  display: flex;
  align-items: center;
  gap: 12px;
  background: var(--vp-white);
  border: 1px solid var(--border-hairline);
  padding: 12px 14px;
}
.giw-projcard__mark {
  width: 12px;
  height: 12px;
  background: var(--vp-paars-100);
  flex-shrink: 0;
}
.giw-projcard__title {
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 14px;
  color: var(--vp-black);
}
.giw-tag {
  display: inline-block;
  margin-top: 4px;
  font-family: var(--font-ui);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--vp-geel-140);
}
.giw-tabs {
  display: flex;
  gap: 6px;
}
.giw-tab {
  font-family: var(--font-ui);
  font-size: 11px;
  color: var(--fg-3);
  padding: 4px 8px;
  border-bottom: 2px solid transparent;
}
.giw-tab--on {
  color: var(--vp-black);
  font-weight: 700;
  border-bottom-color: var(--accent-100);
}

/* chat */
.giw-toggle {
  display: inline-flex;
  gap: 4px;
  background: var(--vp-white);
  border: 1px solid var(--border-hairline);
  padding: 3px;
  align-self: flex-start;
}
.giw-toggle__pill {
  font-family: var(--font-ui);
  font-size: 10px;
  font-weight: 700;
  padding: 3px 10px;
  color: var(--fg-3);
}
.giw-toggle__pill--on {
  background: var(--vp-black);
  color: var(--vp-white);
}
.giw-bubble {
  font-family: var(--font-body);
  font-size: 12px;
  padding: 8px 11px;
  max-width: 85%;
}
.giw-bubble--user {
  align-self: flex-end;
  background: var(--vp-black);
  color: var(--vp-white);
}
.giw-bubble--alice {
  align-self: flex-start;
  background: var(--vp-white);
  border: 1px solid var(--border-hairline);
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 60%;
}
.giw-line {
  height: 6px;
  background: var(--vp-grijs-40);
  display: block;
}
.giw-composer {
  display: flex;
  align-items: center;
  gap: 8px;
  background: var(--vp-white);
  border: 1px solid var(--border-hairline);
  padding: 8px 10px;
}
.giw-composer__slash {
  font-family: var(--font-mono);
  font-weight: 700;
  color: var(--accent-120);
  border: 1px solid var(--border-hairline);
  padding: 0 5px;
  font-size: 11px;
}
.giw-composer__ph {
  font-size: 11px;
  color: var(--fg-3);
}

/* team + notes */
.giw-msg {
  display: flex;
  align-items: center;
  gap: 8px;
}
.giw-msg--me {
  flex-direction: row-reverse;
}
.giw-avatar {
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  color: var(--vp-white);
  font-family: var(--font-ui);
  font-size: 10px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}
.giw-msg__text {
  font-size: 12px;
  background: var(--vp-white);
  border: 1px solid var(--border-hairline);
  padding: 6px 10px;
}
.giw-msg__text--me {
  background: var(--vp-black);
  color: var(--vp-white);
  border-color: var(--vp-black);
}
.giw-note {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 2px;
  background: var(--accent-10);
  border-left: 3px solid var(--accent-100);
  padding: 8px 10px;
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 600;
  color: var(--vp-black);
}

/* budget */
.giw-chip {
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--vp-black);
  border: 1px solid var(--vp-black);
  padding: 6px 12px;
}
.giw-meter {
  width: 80%;
  height: 10px;
  background: var(--vp-grijs-20);
  overflow: hidden;
}
.giw-meter__fill {
  height: 100%;
  background: var(--accent-100);
}
.giw-meter__label {
  font-family: var(--font-ui);
  font-size: 11px;
  color: var(--fg-3);
}

/* ---- Copy ---- */
.giw-body {
  padding: 22px 26px 4px;
}
.giw-eyebrow {
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--fg-3);
  margin-bottom: 8px;
}
.giw-title {
  margin: 0 0 8px;
  font-family: var(--font-display);
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: var(--vp-black);
}
.giw-text {
  margin: 0;
  font-size: 14px;
  line-height: 1.55;
  color: var(--fg-2);
}

/* ---- Footer ---- */
.giw-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 26px 24px;
  gap: 12px;
}
.giw-dots {
  display: flex;
  gap: 6px;
}
.giw-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--vp-grijs-40);
}
.giw-dot--on {
  background: var(--vp-black);
}
.giw-actions {
  display: flex;
  gap: 8px;
}
.giw-btn {
  height: 38px;
  padding: 0 16px;
  background: var(--vp-black);
  color: var(--vp-white);
  border: 1px solid var(--vp-black);
  border-radius: 0;
  cursor: pointer;
  font-family: var(--font-ui);
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  transition: background var(--dur-fast) var(--ease-hover),
    color var(--dur-fast) var(--ease-hover);
}
.giw-btn:hover {
  background: var(--accent-100);
  border-color: var(--accent-100);
  color: var(--vp-black);
}
.giw-btn--ghost {
  background: var(--vp-white);
  color: var(--fg-2);
  border-color: var(--border-hairline);
}
.giw-btn--ghost:hover {
  background: var(--vp-white);
  border-color: var(--vp-black);
  color: var(--vp-black);
}

/* ---- Transition ---- */
.giw-fade-enter-active,
.giw-fade-leave-active {
  transition: opacity var(--dur-base) var(--ease-out);
}
.giw-fade-enter-from,
.giw-fade-leave-to {
  opacity: 0;
}
</style>
